import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Save, Plus, Trash2, GripVertical, ChevronDown, ChevronUp, Eye, EyeOff } from 'lucide-react';

const ICON_OPTIONS = ['Building2', 'Users', 'Key', 'Home', 'Hotel', 'Briefcase', 'Shield', 'Search', 'Hammer', 'DollarSign', 'TrendingUp', 'Target'];
const GRID_OPTIONS = [
  { value: 'lg:col-span-2 lg:row-span-2', label: 'Large (2x2)' },
  { value: 'lg:col-span-1 lg:row-span-1', label: 'Standard (1x1)' },
  { value: 'lg:col-span-2 lg:row-span-1', label: 'Wide (2x1)' },
];

const ServicesSectionTab = ({ authAxios }) => {
  const [data, setData] = useState({
    subtitle: 'Our Solutions',
    headline: 'What We Do',
    services: []
  });
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await authAxios.get('/admin/sections/services');
        if (res.data && res.data.services?.length > 0) {
          setData(prev => ({ ...prev, ...res.data }));
        }
      } catch (e) { console.error(e); }
    };
    fetch();
  }, [authAxios]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await authAxios.put('/admin/sections/services', data);
      toast.success('Services section updated!');
    } catch (e) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const addService = () => {
    const id = `service-${Date.now()}`;
    setData({
      ...data,
      services: [...data.services, { id, title: '', short_title: '', icon: 'Building2', image: '', description: '', features: [], grid_class: 'lg:col-span-1 lg:row-span-1', visible: true }]
    });
    setExpanded(data.services.length);
  };

  const removeService = (i) => {
    setData({ ...data, services: data.services.filter((_, idx) => idx !== i) });
    setExpanded(null);
  };

  const updateService = (i, field, value) => {
    const services = [...data.services];
    services[i] = { ...services[i], [field]: value };
    setData({ ...data, services });
  };

  const updateFeature = (si, fi, value) => {
    const services = [...data.services];
    const features = [...services[si].features];
    features[fi] = value;
    services[si] = { ...services[si], features };
    setData({ ...data, services });
  };

  const addFeature = (si) => {
    const services = [...data.services];
    services[si] = { ...services[si], features: [...services[si].features, ''] };
    setData({ ...data, services });
  };

  const removeFeature = (si, fi) => {
    const services = [...data.services];
    services[si] = { ...services[si], features: services[si].features.filter((_, idx) => idx !== fi) };
    setData({ ...data, services });
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="services-section-tab">
      <div className="bg-[#111111] border border-white/5 p-6">
        <h2 className="font-mono font-bold mb-6">Section Header</h2>
        <div className="space-y-4">
          <div>
            <label className="text-white/50 text-xs font-mono uppercase block mb-2">Subtitle</label>
            <input value={data.subtitle} onChange={e => setData({...data, subtitle: e.target.value})} className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none" />
          </div>
          <div>
            <label className="text-white/50 text-xs font-mono uppercase block mb-2">Headline</label>
            <input value={data.headline} onChange={e => setData({...data, headline: e.target.value})} className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none" />
          </div>
        </div>
      </div>

      <div className="bg-[#111111] border border-white/5 p-6">
        <h2 className="font-mono font-bold mb-6">Services ({data.services.length})</h2>
        <div className="space-y-3">
          {data.services.map((service, i) => (
            <div key={service.id || i} className={`bg-[#0A0A0A] border rounded overflow-hidden ${service.visible === false ? 'border-white/5 opacity-60' : 'border-white/5'}`}>
              <div className="flex items-center">
                <button
                  onClick={() => setExpanded(expanded === i ? null : i)}
                  className="flex-1 flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <GripVertical className="w-4 h-4 text-white/20" />
                    <span className="font-mono text-sm text-white">{service.title || `Service ${i + 1}`}</span>
                    {service.short_title && <span className="font-mono text-xs text-white/30">({service.short_title})</span>}
                    {service.visible === false && <span className="font-mono text-[10px] text-white/30 bg-white/5 px-1.5 py-0.5 rounded">HIDDEN</span>}
                  </div>
                  {expanded === i ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronDown className="w-4 h-4 text-white/40" />}
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); updateService(i, 'visible', service.visible === false ? true : false); }}
                  data-testid={`toggle-service-visible-${i}`}
                  className={`px-3 py-4 border-l border-white/5 transition-colors ${service.visible === false ? 'text-white/30 hover:text-white/60' : 'text-[#D4AF37] hover:text-[#D4AF37]/80'}`}
                  title={service.visible === false ? 'Show on website' : 'Hide from website'}
                >
                  {service.visible === false ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {expanded === i && (
                <div className="p-4 border-t border-white/5 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-white/50 text-xs font-mono uppercase block mb-2">Title</label>
                      <input value={service.title} onChange={e => updateService(i, 'title', e.target.value)} className="w-full bg-[#111111] border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#D4AF37] outline-none" />
                    </div>
                    <div>
                      <label className="text-white/50 text-xs font-mono uppercase block mb-2">Short Title</label>
                      <input value={service.short_title} onChange={e => updateService(i, 'short_title', e.target.value)} className="w-full bg-[#111111] border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#D4AF37] outline-none" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-white/50 text-xs font-mono uppercase block mb-2">Icon</label>
                      <select value={service.icon} onChange={e => updateService(i, 'icon', e.target.value)} className="w-full bg-[#111111] border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#D4AF37] outline-none">
                        {ICON_OPTIONS.map(ic => <option key={ic} value={ic}>{ic}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-white/50 text-xs font-mono uppercase block mb-2">Grid Size</label>
                      <select value={service.grid_class} onChange={e => updateService(i, 'grid_class', e.target.value)} className="w-full bg-[#111111] border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#D4AF37] outline-none">
                        {GRID_OPTIONS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-white/50 text-xs font-mono uppercase block mb-2">Image URL</label>
                    <input value={service.image} onChange={e => updateService(i, 'image', e.target.value)} placeholder="https://..." className="w-full bg-[#111111] border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#D4AF37] outline-none" />
                    {service.image && <img src={service.image} alt="" className="mt-2 h-20 w-32 object-cover rounded border border-white/10" />}
                  </div>
                  <div>
                    <label className="text-white/50 text-xs font-mono uppercase block mb-2">Description</label>
                    <textarea value={service.description} onChange={e => updateService(i, 'description', e.target.value)} rows={3} className="w-full bg-[#111111] border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#D4AF37] outline-none resize-none" />
                  </div>
                  <div>
                    <label className="text-white/50 text-xs font-mono uppercase block mb-2">Features</label>
                    {service.features.map((f, fi) => (
                      <div key={fi} className="flex items-center gap-2 mb-2">
                        <input value={f} onChange={e => updateFeature(i, fi, e.target.value)} className="flex-1 bg-[#111111] border border-white/10 px-3 py-2 font-mono text-xs focus:border-[#D4AF37] outline-none" />
                        <button onClick={() => removeFeature(i, fi)} className="p-1 text-red-400/60 hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
                      </div>
                    ))}
                    <button onClick={() => addFeature(i)} className="text-[#D4AF37] font-mono text-xs hover:underline">+ Add Feature</button>
                  </div>
                  <button onClick={() => removeService(i)} className="flex items-center gap-2 text-red-400/60 hover:text-red-400 font-mono text-xs mt-2">
                    <Trash2 className="w-3.5 h-3.5" />Remove Service
                  </button>
                </div>
              )}
            </div>
          ))}
          <button onClick={addService} data-testid="add-service" className="flex items-center gap-2 px-4 py-2.5 border border-dashed border-white/10 text-white/50 hover:text-white hover:border-white/30 transition-colors font-mono text-xs w-full justify-center">
            <Plus className="w-4 h-4" />Add Service
          </button>
        </div>
      </div>

      <button onClick={handleSave} disabled={saving} data-testid="save-services-section" className="flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-black font-mono text-sm hover:bg-[#D4AF37]/90 transition-colors disabled:opacity-50">
        <Save className="w-4 h-4" />{saving ? 'Saving...' : 'Save Services'}
      </button>
    </div>
  );
};

export default ServicesSectionTab;
